/**
 * Warnings Client Library 1.2.0
 * A static class for fetching and rendering product warnings from the CKO API
 * @class WarningsClient
 */
class WarningsClient {

    static config = {
        storeName: 'geaprstore',
        secret: '9MyI469eP3KZm7ATcAmHm9IQYSR0v9OHE0AkXsnOMOdrFi8OrSg8_McPy3vY55yC',
        api: 'https://cko-proxy.569studios.com/cko-proxy/',
        warningsRoute: 'apps/cko/warnings',
        simpleSelector: '',
        relationalSelector: '',
        debug: false
    };

    static shop = '';
    static sku = '';
    static productCache = null;

    /**
     * Initialize the Warnings client
     * @param {Object} options - Configuration options
     * @param {string} options.sku - The product SKU
     * @param {string} options.simpleSelector - CSS selector for SIMPLE warnings container
     * @param {string} options.relationalSelector - CSS selector for RELATIONAL warnings container
     */
    static async init(options = {}) {
        this.shop = Shopify.shop || '';

        if (options.sku) this.sku = options.sku;
        if (options.simpleSelector) this.config.simpleSelector = options.simpleSelector;
        if (options.relationalSelector) this.config.relationalSelector = options.relationalSelector;

        this.config.debug && console.log('Warnings Client initialized for shop:', this.shop);

        if (this.sku && (this.config.simpleSelector || this.config.relationalSelector)) {
            await this.renderWarnings();
        }
    }

    /**
     * Render warnings in the configured selectors
     * @returns {Promise<void>}
     */
    static async renderWarnings() {
        const result = await this.getWarnings(this.sku);

        this.config.debug && console.log(result);

        if (!result || !result.data || !Array.isArray(result.data)) {
            this.config.debug && console.log('No warnings to render');
            return;
        }

        result.data.forEach(warning => {
            if (warning.warning_type === 'SIMPLE') {
                this.renderSimpleWarning(warning);
            } else if (warning.warning_type === 'RELATIONAL') {
                this.renderRelationalWarning(warning);
            }
        });
    }

    /**
     * Render a SIMPLE warning message
     * @param {Object} warning - The warning object
     */
    static renderSimpleWarning(warning) {
        if (!this.config.simpleSelector) {
            this.config.debug && console.log('No simpleSelector configured');
            return;
        }

        const container = document.querySelector(this.config.simpleSelector);
        if (!container) {
            console.error('Simple warning container not found:', this.config.simpleSelector);
            return;
        }

        const warningElement = document.createElement('p');
        warningElement.className = 'warning-simple';
        warningElement.textContent = warning.warning_message;
        container.appendChild(warningElement);

        this.config.debug && console.log('Rendered SIMPLE warning:', warning.warning_message);
    }

    /**
     * Render a RELATIONAL warning with product add-to-cart functionality
     * @param {Object} warning - The warning object containing relatedSkus
     */
    static async renderRelationalWarning(warning) {
        if (!this.config.relationalSelector) {
            this.config.debug && console.log('No relationalSelector configured');
            return;
        }

        const container = document.querySelector(this.config.relationalSelector);
        if (!container) {
            console.error('Relational warning container not found:', this.config.relationalSelector);
            return;
        }

        const warningDiv = document.createElement('div');
        warningDiv.className = 'warning-relational';

        const optionalSkus = warning.relatedSkus?.filter(s => s.sku_type === 'OPTIONAL') || [];
        const requiredSkus = warning.relatedSkus?.filter(s => s.sku_type === 'REQUIRED') || [];

        let formattedMessage = warning.warning_message;
        warning.relatedSkus?.forEach(related => {
            formattedMessage = formattedMessage.replace(
                new RegExp(related.sku, 'g'),
                `<strong>${related.sku}</strong>`
            );
        });

        for (const optionalSku of optionalSkus) {
            const productInfo = await this.getProductBySku(optionalSku.sku);
            if (!productInfo) {
                this.config.debug && console.log('Could not fetch product info for:', optionalSku.sku);
                continue;
            }

            const requiredProducts = [];
            for (const reqSku of requiredSkus) {
                const reqInfo = await this.getProductBySku(reqSku.sku);
                if (reqInfo) requiredProducts.push(reqInfo);
            }

            const rowDiv = document.createElement('div');
            rowDiv.className = 'warning-relational__row';
            rowDiv.style.cssText = 'display:block !important;background:#f5f5f5;border-radius:6px;padding:12px;margin-top:12px;max-width:100%;overflow:hidden;box-sizing:border-box;';

            const msgP = document.createElement('p');
            msgP.style.cssText = 'display:block !important;color:#BF5B30;margin:0 0 10px 0;font-size:13px;line-height:1.4;width:100%;max-width:100%;word-wrap:break-word;overflow-wrap:break-word;white-space:normal !important;';
            msgP.innerHTML = formattedMessage;
            rowDiv.appendChild(msgP);

            const productRow = document.createElement('div');
            productRow.style.cssText = 'display:flex !important;align-items:center;justify-content:space-between;gap:12px;width:100%;';

            const productDiv = document.createElement('div');
            productDiv.style.cssText = 'display:flex !important;align-items:center;gap:8px;';
            productDiv.innerHTML = `<span style="font-size:14px;color:#333;">${productInfo.title}</span><span style="font-size:14px;font-weight:600;color:#333;">$${productInfo.price}</span>`;
            productRow.appendChild(productDiv);

            const addBtn = document.createElement('button');
            addBtn.type = 'button';
            addBtn.className = 'btn btn--primary warning-relational__add-btn';
            addBtn.style.cssText = 'padding:8px 16px;font-size:13px;white-space:nowrap;flex-shrink:0;';
            addBtn.textContent = 'Add to Cart';
            addBtn.addEventListener('click', (e) => this.handleAddToCart(e, productInfo, requiredProducts));
            productRow.appendChild(addBtn);

            rowDiv.appendChild(productRow);
            warningDiv.appendChild(rowDiv);
        }

        container.appendChild(warningDiv);
        this.config.debug && console.log('Rendered RELATIONAL warning:', warning.warning_message);
    }

    /**
     * Handle Add to Cart for OPTIONAL + REQUIRED products
     * @param {Event} e - Click event
     * @param {Object} optionalProduct - The optional product info
     * @param {Array} requiredProducts - Array of required product info
     */
    static async handleAddToCart(e, optionalProduct, requiredProducts) {
        e.preventDefault();
        e.stopPropagation();

        const btn = e.target;
        btn.disabled = true;
        btn.textContent = 'Adding...';

        try {
            const items = [{ id: optionalProduct.variant_id, quantity: 1 }];
            requiredProducts.forEach(req => {
                items.push({ id: req.variant_id, quantity: 1 });
            });

            await axios.post('/cart/add.js', { items }, {
                headers: { 'Content-Type': 'application/json' }
            });

            btn.textContent = 'Added!';
            setTimeout(() => window.location.reload(), 500);

        } catch (error) {
            console.error('Error adding to cart:', error);
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
        }
    }

    /**
     * Get warnings for a specific SKU
     * @param {string} sku - The product SKU to get warnings for
     * @returns {Promise<Object>} - The warnings data
     */
    static async getWarnings(sku) {
        if (!sku) {
            console.error('SKU is required');
            return null;
        }

        if (!this.shop) {
            console.error('Shop not set. Call init() first.');
            return null;
        }

        try {
            const response = await axios.post(
                `${this.config.api}${this.config.warningsRoute}`,
                {
                    shop: this.shop,
                    secret: this.config.secret,
                    sku: sku
                }
            );

            this.config.debug && console.log('Warnings API response:', response.data);
            return response.data;

        } catch (error) {
            console.error('Error fetching warnings:', error);
            return null;
        }
    }

    /**
     * Check if a product has warnings
     * @param {string} sku - The product SKU to check
     * @returns {Promise<boolean>} - True if the product has warnings
     */
    static async hasWarnings(sku) {
        const result = await this.getWarnings(sku);
        return result?.data?.length > 0 || false;
    }

    /**
     * Get warnings filtered by type
     * @param {string} sku - The product SKU
     * @param {string} type - The warning type ('SIMPLE' or 'RELATIONAL')
     * @returns {Promise<Array>} - Array of warnings of the specified type
     */
    static async getWarningsByType(sku, type) {
        if (type !== 'SIMPLE' && type !== 'RELATIONAL') {
            console.error('Invalid warning type. Use "SIMPLE" or "RELATIONAL".');
            return [];
        }

        const result = await this.getWarnings(sku);
        return result?.data?.filter(w => w.warning_type === type) || [];
    }

    /**
     * Enable debug mode
     */
    static enableDebug() {
        this.config.debug = true;
        console.log('Warnings Client debug mode enabled');
    }

    /**
     * Disable debug mode
     */
    static disableDebug() {
        this.config.debug = false;
    }

    /**
     * Load all products into cache for SKU lookup
     * @returns {Promise<void>}
     */
    static async loadProductCache() {
        if (this.productCache) return;

        this.productCache = new Map();

        try {
            let page = 1;
            let hasMore = true;

            while (hasMore && page <= 10) {
                const response = await axios.get(`/collections/all/products.json?limit=250&page=${page}`);
                const products = response.data?.products || [];

                if (products.length === 0) {
                    hasMore = false;
                    break;
                }

                for (const product of products) {
                    for (const variant of product.variants || []) {
                        if (variant.sku) {
                            this.productCache.set(variant.sku, {
                                id: product.id,
                                title: product.title,
                                handle: product.handle,
                                image: product.images?.[0]?.src || '',
                                variant_id: variant.id,
                                price: variant.price,
                                sku: variant.sku
                            });
                        }
                    }
                }
                page++;
            }

            this.config.debug && console.log('Product cache loaded:', this.productCache.size, 'SKUs');
        } catch (error) {
            console.error('Error loading product cache:', error);
        }
    }

    /**
     * Get product info from Shopify by SKU
     * @param {string} sku - The product SKU
     * @returns {Promise<Object|null>} - Product info with variant_id, title, price, image
     */
    static async getProductBySku(sku) {
        await this.loadProductCache();
        const product = this.productCache?.get(sku);

        if (product) return product;

        this.config.debug && console.log('Product not found for SKU:', sku);
        return null;
    }
}